'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Cog,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  ShieldCheck,
  GraduationCap,
  Sparkles,
  Mail,
  KeyRound,
  RefreshCw,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export default function RegisterPage() {
  const router = useRouter();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    nim: '',
    name: '',
    email: '',
    phone: '',
    entry_year: new Date().getFullYear() - 5,
    graduation_year: new Date().getFullYear() - 1,
    cohort_name: '',
    birth_date: '1998-01-01',
    password: '',
    password_confirmation: '',
    agree_terms: false,
  });

  // OTP Verification States
  const [otpCode, setOtpCode] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [debugOtp, setDebugOtp] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successStatus, setSuccessStatus] = useState<string | null>(null);

  // Countdown timer effect
  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      if (name === 'nim') {
        const cleanNim = value.trim();
        let detectedYear = formData.entry_year;
        let detectedCohort = formData.cohort_name;

        if (cleanNim.length >= 2) {
          const prefix = cleanNim.substring(0, 2);
          const num = parseInt(prefix, 10);
          if (!isNaN(num)) {
            detectedYear = num > 60 ? 1900 + num : 2000 + num;
            detectedCohort = String(detectedYear);
          }
        }
        setFormData((prev) => ({
          ...prev,
          nim: value,
          entry_year: detectedYear,
          cohort_name: detectedCohort,
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }

      if (name === 'email') {
        // Reset OTP if email changed
        setIsOtpVerified(false);
        setIsOtpSent(false);
        setOtpCode('');
        setDebugOtp(null);
      }
    }
  };

  const handleSendOtp = async () => {
    if (!formData.email || !formData.email.includes('@')) {
      toast.error('Masukkan alamat email yang valid terlebih dahulu.');
      return;
    }

    setIsSendingOtp(true);
    setErrorMessage('');

    try {
      const res = await api.post<{
        success: boolean;
        message: string;
        debug_code?: string;
      }>('/auth/send-verification-code', { email: formData.email });

      setIsOtpSent(true);
      setCountdown(60);
      if (res.debug_code) {
        setDebugOtp(res.debug_code);
        setOtpCode(res.debug_code);
      }
      toast.success(res.message || 'Kode verifikasi telah dikirim ke email Anda!');
    } catch (err: any) {
      const msg = err.message || 'Gagal mengirim kode verifikasi.';
      toast.error(msg);
      setErrorMessage(msg);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Masukkan 6 digit kode verifikasi.');
      return;
    }

    setIsVerifyingOtp(true);
    setErrorMessage('');

    try {
      const res = await api.post<{
        success: boolean;
        message: string;
        verified: boolean;
      }>('/auth/verify-code', { email: formData.email, code: otpCode });

      if (res.verified) {
        setIsOtpVerified(true);
        toast.success('Email berhasil diverifikasi!');
      }
    } catch (err: any) {
      const msg = err.message || 'Kode verifikasi tidak valid.';
      toast.error(msg);
      setErrorMessage(msg);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!isOtpVerified) {
      setErrorMessage('Harap verifikasi email Anda dengan kode OTP terlebih dahulu.');
      toast.error('Harap verifikasi email Anda dengan kode OTP terlebih dahulu.');
      return;
    }

    if (formData.password !== formData.password_confirmation) {
      setErrorMessage('Konfirmasi password tidak cocok.');
      return;
    }

    if (!formData.agree_terms) {
      setErrorMessage('Anda wajib menyetujui Anggaran Dasar / Ketentuan IKA Mesin ITN Malang.');
      return;
    }

    setIsLoading(true);

    try {
      const res = await register({
        ...formData,
        entry_year: Number(formData.entry_year),
        graduation_year: formData.graduation_year ? Number(formData.graduation_year) : undefined,
        verification_code: otpCode,
      });

      setSuccessStatus(res.verification_status);
      toast.success(res.message || 'Registrasi berhasil diproses!');
    } catch (err: any) {
      const msg = err.message || 'Terjadi kesalahan saat registrasi. Periksa kembali data Anda.';
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  if (successStatus) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center px-4 py-12 bg-[#F5F7FA] dark:bg-[#06182C] relative selection:bg-[#D71920] selection:text-white">
        <Card className="w-full max-w-lg border border-[#E2E8F0] dark:border-slate-800 bg-white dark:bg-[#0A2544] shadow-corporate-lg rounded-xl p-8 text-center space-y-6">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-200">
            <CheckCircle2 className="h-10 w-10" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-bold text-[#082B52] dark:text-white">
              Pendaftaran Berhasil Diajukan!
            </h2>
            <p className="text-xs sm:text-sm text-[#64748B] dark:text-slate-300 leading-relaxed">
              {successStatus === 'verified'
                ? 'NIM Anda cocok dengan Master Data Akademik Teknik Mesin ITN Malang. Akun Anda telah terverifikasi otomatis!'
                : 'Data pendaftaran Anda telah kami terima. Tim Pengurus IKA Mesin ITN Malang sedang memverifikasi NIM dan riwayat akademik Anda.'}
            </p>
          </div>

          <div className="p-4 bg-[#F5F7FA] dark:bg-slate-900/60 rounded-lg text-xs text-[#64748B] dark:text-slate-400 space-y-1 text-left border border-[#E2E8F0] dark:border-slate-800">
            <p className="font-bold text-[#082B52] dark:text-white">NIM: {formData.nim}</p>
            <p>Nama: {formData.name}</p>
            <p>Email: {formData.email}</p>
          </div>

          <Button asChild className="w-full rounded-lg bg-[#D71920] hover:bg-[#B8141B] text-white font-bold h-11 text-xs sm:text-sm shadow-sm transition-colors">
            <Link href="/login">Menuju Halaman Masuk</Link>
          </Button>
        </Card>
      </div>
    );
  }

  // Generate Year options (1975 to current year)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1974 }, (_, i) => currentYear - i);

  return (
    <div className="min-h-screen flex flex-col justify-center items-center px-4 py-12 bg-[#F5F7FA] dark:bg-[#06182C] relative selection:bg-[#D71920] selection:text-white">
      {/* Back to Home button */}
      <div className="w-full max-w-2xl mb-6 flex justify-between items-center">
        <Button asChild variant="ghost" size="sm" className="rounded-lg text-[#64748B] hover:text-[#082B52] dark:hover:text-white text-xs">
          <Link href="/">
            <ArrowLeft className="h-4 w-4 mr-1.5 text-[#D71920]" />
            Kembali ke Beranda
          </Link>
        </Button>

        <span className="text-[11px] font-bold text-[#004A99] dark:text-blue-400 uppercase tracking-wider">
          Formulir Alumni Baru
        </span>
      </div>

      <Card className="w-full max-w-2xl border border-[#E2E8F0] dark:border-slate-800 bg-white dark:bg-[#0A2544] shadow-corporate-lg rounded-xl relative z-10 overflow-hidden">
        {/* Top Accent Bar: Red #D71920 */}
        <div className="h-1.5 w-full bg-[#D71920]" />
        <CardHeader className="text-center space-y-2 pb-6 border-b border-[#E2E8F0] dark:border-slate-800">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-white p-1 shadow-xs border border-[#E2E8F0] overflow-hidden">
            <Image
              src="/images/logo-ika-mesin.png"
              alt="Logo IKA MESIN ITN MALANG"
              width={48}
              height={48}
              className="object-contain"
              priority
            />
          </div>
          <CardTitle className="text-xl sm:text-2xl font-bold tracking-tight text-[#082B52] dark:text-white">
            Registrasi Alumni Baru
          </CardTitle>
          <CardDescription className="text-xs text-[#64748B] dark:text-slate-400 max-w-md mx-auto">
            Khusus Lulusan Program Studi Teknik Mesin S1 & D3 Institut Teknologi Nasional Malang.
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {errorMessage && (
              <Alert variant="destructive" className="rounded-xl text-xs py-2.5">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            {/* Section 1: Data Akademik */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground border-b border-border/40 pb-2">
                <GraduationCap className="h-4 w-4 text-blue-600" />
                <span>1. Data Akademik Teknik Mesin ITN</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="nim" className="text-xs font-semibold">
                    Nomor Induk Mahasiswa (NIM) <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="nim"
                    name="nim"
                    type="text"
                    placeholder="Contoh: 1921001"
                    value={formData.nim}
                    onChange={handleChange}
                    required
                    className="rounded-xl font-mono text-sm focus-visible:ring-red-500"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Akan dicocokkan otomatis dengan master data akademik prodi.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-xs font-semibold">
                    Nama Lengkap (Sesuai Ijazah) <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Contoh: Budi Santoso, S.T."
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="rounded-xl text-sm focus-visible:ring-red-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="entry_year" className="text-xs font-semibold">
                    Tahun Angkatan (Masuk) <span className="text-red-600">*</span>
                  </Label>
                  <select
                    id="entry_year"
                    name="entry_year"
                    value={formData.entry_year}
                    onChange={handleChange}
                    required
                    className="w-full h-10 px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    {years.map((y) => (
                      <option key={y} value={y}>
                        Angkatan {y}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="graduation_year" className="text-xs font-semibold">
                    Tahun Kelulusan
                  </Label>
                  <select
                    id="graduation_year"
                    name="graduation_year"
                    value={formData.graduation_year}
                    onChange={handleChange}
                    className="w-full h-10 px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {years.map((y) => (
                      <option key={y} value={y}>
                        Lulus {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Section 2: Kontak & Identitas */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground border-b border-border/40 pb-2">
                <ShieldCheck className="h-4 w-4 text-red-600" />
                <span>2. Kontak & Keamanan Akun</span>
              </div>

              <div className="space-y-4">
                {/* Email Field with Send Code Button */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="email" className="text-xs font-semibold">
                      Alamat Email Aktif <span className="text-red-600">*</span>
                    </Label>
                    {isOtpVerified ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                        <Check className="w-3 h-3" /> Email Terverifikasi
                      </span>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">
                        Kode 6 digit akan dikirim ke email ini
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="alumni@gmail.com"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={isOtpVerified}
                        required
                        className="rounded-xl text-sm pl-9 focus-visible:ring-red-500"
                      />
                    </div>
                    {!isOtpVerified && (
                      <Button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={isSendingOtp || countdown > 0 || !formData.email}
                        variant="outline"
                        className="rounded-xl text-xs font-bold shrink-0 border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-500/10"
                      >
                        {isSendingOtp ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                        ) : countdown > 0 ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" />
                        ) : (
                          <KeyRound className="w-3.5 h-3.5 mr-1 text-red-600" />
                        )}
                        {countdown > 0 ? `Kirim Ulang (${countdown}s)` : isOtpSent ? 'Kirim Ulang' : 'Kirim Kode'}
                      </Button>
                    )}
                  </div>
                </div>

                {/* OTP Input Box (Displayed when OTP is sent and not yet verified) */}
                {isOtpSent && !isOtpVerified && (
                  <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/20 space-y-3 animate-in fade-in slide-in-from-top-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="otpCode" className="text-xs font-bold text-foreground flex items-center gap-1.5">
                        <KeyRound className="w-3.5 h-3.5 text-red-600" /> Masukkan Kode Verifikasi (OTP)
                      </Label>
                      {debugOtp && (
                        <span className="text-[11px] font-mono text-red-600 dark:text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">
                          Simulasi OTP: {debugOtp}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        id="otpCode"
                        type="text"
                        maxLength={6}
                        placeholder="6 Digit Kode (misal: 123456)"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                        className="rounded-xl text-center text-base tracking-widest font-mono font-bold bg-background focus-visible:ring-red-500"
                      />
                      <Button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={isVerifyingOtp || otpCode.length !== 6}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold shrink-0"
                      >
                        {isVerifyingOtp ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                        ) : (
                          <Check className="w-3.5 h-3.5 mr-1" />
                        )}
                        Verifikasi
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Cek kotak masuk atau folder spam email Anda. Kode berlaku selama 15 menit.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-xs font-semibold">
                      Nomor WhatsApp / HP <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="081234567890"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="rounded-xl text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="birth_date" className="text-xs font-semibold">
                  Tanggal Lahir (Untuk Verifikasi Identitas) <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="birth_date"
                  name="birth_date"
                  type="date"
                  value={formData.birth_date}
                  onChange={handleChange}
                  required
                  className="rounded-xl text-sm max-w-xs"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-xs font-semibold">
                    Password <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Minimal 8 karakter"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="rounded-xl text-sm"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password_confirmation" className="text-xs font-semibold">
                    Konfirmasi Password <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="password_confirmation"
                    name="password_confirmation"
                    type="password"
                    placeholder="Ulangi password"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="rounded-xl text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Terms checkbox */}
            <div className="flex items-start space-x-2.5 pt-2">
              <input
                type="checkbox"
                id="agree_terms"
                name="agree_terms"
                checked={formData.agree_terms}
                onChange={handleChange}
                required
                className="mt-1 rounded border-border text-red-600 focus:ring-red-500 h-4 w-4"
              />
              <label htmlFor="agree_terms" className="text-xs text-muted-foreground leading-relaxed cursor-pointer select-none">
                Saya menyatakan bahwa saya adalah alumni resmi Program Studi Teknik Mesin ITN Malang dan bersedia mematuhi Anggaran Dasar & Anggaran Rumah Tangga (AD/ART) IKA Mesin ITN Malang.
              </label>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold shadow-lg shadow-red-600/25 h-12 text-sm"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Memverifikasi NIM & Mendaftarkan...
                </>
              ) : (
                'Ajukan Pendaftaran Alumni'
              )}
            </Button>
          </form>
        </CardContent>

        <CardFooter className="flex justify-center border-t border-border/40 py-4">
          <p className="text-xs text-muted-foreground">
            Sudah memiliki akun terverifikasi?{' '}
            <Link href="/login" className="font-bold text-blue-600 hover:text-blue-700">
              Masuk ke Akun
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
